
import React, { useState, useEffect, useId } from 'react';
import { AppState, BetOutcome, Bet } from '../types.ts';
import { X, Target, Zap, Calendar, Users, BarChart3 } from 'lucide-react';

interface AddBetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (bet: any) => void;
  state: AppState;
  initialData?: Bet | null;
}

const AddBetModal: React.FC<AddBetModalProps> = ({ isOpen, onClose, onAdd, state, initialData }) => {
  const dateInputId = useId();
  const [formData, setFormData] = useState({
    leagueId: '',
    player1Id: '',
    player2Id: '',
    marketId: '',
    stakeRaw: '',
    oddRaw: '',
    player1Goals: '',
    player2Goals: '',
    outcome: BetOutcome.PENDING,
    date: new Date().toISOString().split('T')[0]
  });

  const formatMask = (value: string) => {
    const cleanValue = value.toString().replace(/\D/g, '');
    if (!cleanValue) return '';
    const numberValue = parseInt(cleanValue) / 100;
    return numberValue.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData({
          leagueId: initialData.league_id,
          player1Id: initialData.player1_id,
          player2Id: initialData.player2_id,
          marketId: initialData.market_id,
          stakeRaw: (initialData.stake * 100).toString(),
          oddRaw: (initialData.odd * 100).toString(),
          player1Goals: initialData.player1_goals?.toString() || '',
          player2Goals: initialData.player2_goals?.toString() || '',
          outcome: initialData.outcome,
          date: initialData.date
        });
      } else {
        const firstLeague = state.leagues[0]?.id || '';
        const leaguePlayers = state.players.filter(p => p.league_id === firstLeague);

        setFormData({
          leagueId: firstLeague,
          player1Id: leaguePlayers[0]?.id || '',
          player2Id: leaguePlayers[1]?.id || leaguePlayers[0]?.id || '',
          marketId: state.markets[0]?.id || '',
          stakeRaw: '',
          oddRaw: '',
          player1Goals: '',
          player2Goals: '',
          outcome: BetOutcome.PENDING,
          date: new Date().toISOString().split('T')[0]
        });
      }
    }
  }, [isOpen, initialData, state.leagues, state.markets]);

  const handleInputChange = (field: string, value: string) => {
    const digits = value.replace(/\D/g, '');
    setFormData(prev => ({ ...prev, [field]: digits }));
  };

  const parseFormattedValue = (formatted: string) => {
    if (!formatted) return 0;
    return parseFloat(formatted.replace(/,/g, ''));
  };

  const formatDateDisplay = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const stakeValue = parseFormattedValue(formatMask(formData.stakeRaw));
    const oddValue = parseFormattedValue(formatMask(formData.oddRaw));

    if (stakeValue <= 0 || oddValue <= 0) {
      alert("Valores inválidos. Verifique Stake e Odd.");
      return;
    }

    onAdd({
      league_id: formData.leagueId,
      player1_id: formData.player1Id,
      player2_id: formData.player2Id,
      market_id: formData.marketId,
      outcome: formData.outcome,
      date: formData.date,
      user_id: state.currentUser.id,
      stake: stakeValue,
      odd: oddValue,
      player1_goals: formData.player1Goals ? parseInt(formData.player1Goals) : 0,
      player2_goals: formData.player2Goals ? parseInt(formData.player2Goals) : 0
    });
    onClose();
  };

  const filteredPlayers = state.players.filter(p => p.league_id === formData.leagueId);
  const playersForP1 = filteredPlayers.filter(p => p.id !== formData.player2Id);
  const playersForP2 = filteredPlayers.filter(p => p.id !== formData.player1Id);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0b0e11]/95 backdrop-blur-md p-4 animate-in fade-in duration-300">
      <div className="bg-[#1e2329] w-full max-w-2xl rounded-[32px] border border-[#474d57] overflow-hidden shadow-[0_32px_80px_rgba(0,0,0,0.8)] border-t-[#fbbf24]/40 relative">

        <div className="p-6 border-b border-[#474d57] flex items-center justify-between bg-gradient-to-b from-[#2b3139]/40 to-transparent">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#00ff66] flex items-center justify-center text-[#0b0e11] rotate-3 shadow-[0_0_20px_rgba(0,255,102,0.3)]">
              <Target size={26} />
            </div>
            <div>
              <h3 className="text-xl font-black tracking-tight uppercase text-white">{initialData ? 'Editar Operação' : 'Nova Entrada'}</h3>
              <p className="text-[10px] text-[#b1b5c0] font-black tracking-widest uppercase">E-Soccer Analytics</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#2b3139] rounded-xl transition-all text-[#b1b5c0] hover:text-white">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6 max-h-[85vh] overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2 space-y-2">
              <label className="text-[10px] text-[#b1b5c0] font-black uppercase tracking-widest flex items-center gap-1.5">
                <BarChart3 size={12} className="text-[#00ff66]" /> Competição
              </label>
              <select
                value={formData.leagueId}
                onChange={e => setFormData({ ...formData, leagueId: e.target.value })}
                className="w-full bg-[#0b0e11] border border-[#474d57] rounded-2xl p-4 text-sm font-bold focus:border-[#00ff66] outline-none appearance-none cursor-pointer text-white"
              >
                {state.leagues.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] text-[#b1b5c0] font-black uppercase tracking-widest flex items-center gap-1.5">
                <Calendar size={12} className="text-[#00ff66]" /> Data
              </label>
              {/* Seletor de Data 100% Clicável */}
              <div
                className="w-full bg-[#0b0e11] border border-[#474d57] rounded-2xl p-4 text-sm font-bold hover:bg-[#2b3139]/40 transition-all text-white flex items-center gap-3 relative cursor-pointer group h-14 overflow-hidden"
                onClick={(e) => (e.currentTarget.querySelector('input') as HTMLInputElement)?.showPicker?.()}
              >
                <Calendar size={16} className="text-[#00ff66] pointer-events-none" />
                <span className="flex-1 font-black pointer-events-none">{formatDateDisplay(formData.date)}</span>
                <input
                  type="date"
                  value={formData.date}
                  onChange={e => setFormData({ ...formData, date: e.target.value })}
                  className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-[10px] text-[#b1b5c0] font-black uppercase tracking-widest flex items-center gap-1.5">
                <Users size={12} className="text-[#00ff66]" /> Player 1
              </label>
              <select
                value={formData.player1Id}
                onChange={e => setFormData({ ...formData, player1Id: e.target.value })}
                className="w-full bg-[#0b0e11] border border-[#474d57] rounded-2xl p-4 text-sm font-bold focus:border-[#00ff66] outline-none text-white"
              >
                {playersForP1.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] text-[#b1b5c0] font-black uppercase tracking-widest flex items-center gap-1.5">
                <Users size={12} className="text-[#00ff66]" /> Player 2
              </label>
              <select
                value={formData.player2Id}
                onChange={e => setFormData({ ...formData, player2Id: e.target.value })}
                className="w-full bg-[#0b0e11] border border-[#474d57] rounded-2xl p-4 text-sm font-bold focus:border-[#00ff66] outline-none text-white"
              >
                {playersForP2.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          </div>

          <div className="bg-[#0b0e11] p-6 rounded-3xl border border-[#474d57]/30 space-y-4">
            <label className="text-[10px] text-[#00ff66] font-black uppercase tracking-widest text-center block">Placar Final (Opcional)</label>
            <div className="flex items-center justify-center gap-8">
              <div className="flex flex-col items-center gap-2">
                <input
                  type="text" maxLength={2} placeholder="0"
                  value={formData.player1Goals}
                  onChange={e => handleInputChange('player1Goals', e.target.value)}
                  className="w-16 bg-[#1e2329] border border-[#474d57] rounded-xl py-3 text-center text-xl font-black text-white outline-none focus:border-[#fbbf24]"
                />
              </div>
              <div className="text-2xl font-black text-[#474d57]">X</div>
              <div className="flex flex-col items-center gap-2">
                <input
                  type="text" maxLength={2} placeholder="0"
                  value={formData.player2Goals}
                  onChange={e => handleInputChange('player2Goals', e.target.value)}
                  className="w-16 bg-[#1e2329] border border-[#474d57] rounded-xl py-3 text-center text-xl font-black text-white outline-none focus:border-[#00ff66]"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 py-4 border-y border-[#474d57]/30">
            <div className="md:col-span-2 space-y-2">
              <label className="text-[10px] text-[#b1b5c0] font-black uppercase tracking-widest">Mercado</label>
              <select
                value={formData.marketId}
                onChange={e => setFormData({ ...formData, marketId: e.target.value })}
                className="w-full bg-[#0b0e11] border border-[#474d57] rounded-2xl p-4 text-sm font-bold focus:border-[#00ff66] outline-none text-white"
              >
                {state.markets.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] text-[#b1b5c0] font-black uppercase tracking-widest">Stake ($)</label>
              <input
                type="text" placeholder="0.00"
                className="w-full bg-[#0b0e11] border border-[#474d57] rounded-2xl px-4 py-4 text-xl font-mono font-bold outline-none text-center text-[#00ff66]"
                value={formatMask(formData.stakeRaw)}
                onChange={e => handleInputChange('stakeRaw', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] text-[#b1b5c0] font-black uppercase tracking-widest">Odd (@)</label>
              <input
                type="text" placeholder="1.00"
                className="w-full bg-[#0b0e11] border border-[#474d57] rounded-2xl px-4 py-4 text-xl font-mono font-bold outline-none text-center text-[#00ff66]"
                value={formatMask(formData.oddRaw)}
                onChange={e => handleInputChange('oddRaw', e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-4">
            <label className="text-[10px] text-[#b1b5c0] font-black uppercase tracking-widest text-center block">Resultado</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { type: BetOutcome.GREEN, label: 'GREEN', color: 'border-[#00ff66] text-[#00ff66]' },
                { type: BetOutcome.RED, label: 'RED', color: 'border-[#f6465d] text-[#f6465d]' },
                { type: BetOutcome.REFUND, label: 'PUSH', color: 'border-[#4788ff] text-[#4788ff]' },
                { type: BetOutcome.PENDING, label: 'PENDENTE', color: 'border-[#b1b5c0] text-[#b1b5c0]' }
              ].map(item => (
                <button
                  key={item.type}
                  type="button"
                  onClick={() => setFormData({ ...formData, outcome: item.type })}
                  className={`py-4 px-2 text-[10px] font-black rounded-2xl border-2 transition-all ${formData.outcome === item.type
                    ? `bg-white text-[#0b0e11] ${item.color.replace('text-', 'border-')}`
                    : `${item.color} bg-transparent opacity-40 hover:opacity-100`
                    }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <button type="submit" className="w-full bg-[#00ff66] text-[#0b0e11] font-black py-6 rounded-[20px] text-base hover:shadow-[0_8px_30px_rgba(0,255,102,0.3)] transition-all uppercase tracking-widest flex items-center justify-center gap-2">
            <Zap size={20} fill="currentColor" /> {initialData ? 'Atualizar Operação' : 'Gravar Operação'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddBetModal;
